import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms"
import {Component, Inject, Input} from "@angular/core"
import { TranslateService } from "../../services/translate.service"
import { AuthService } from "../../services/auth.service"
import { DialogButtonComponent } from "../buttons/dialog-button.component"
import { CommonModule } from "@angular/common"
import {BottomSheetService} from '../../services/bottom-sheet.service';
import {ActionButtonComponent} from '../buttons/action-button.component';
import {LoadingSpinnerComponent} from './loading-spinner-component';
import {AuthorizedApiService} from '../../services/authorized-api.service';
import {LoyaltyConfigProgram} from '../../models/FSConfig';
import {AppStateService} from '../../services/app-state.service';
import {CheckboxLabelComponent} from './checkbox-label.component';

@Component({
  selector: 'app-signup-loyalty',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DialogButtonComponent,
    CommonModule,
    ActionButtonComponent,
    LoadingSpinnerComponent,
    CheckboxLabelComponent,
    FormsModule,
  ],
  template: `
    <div class="mt-1 relative">
      <div class="" [formGroup]="form">
        <div class="mb-1 font-bold">{{ getString("Namn") }}</div>
        <input type="text" formControlName="name" [placeholder]="getString('Anna Andersson')" class="text-input"/>

        <div class="my-1 font-bold">{{ getString("E-post") }}</div>
        <input type="email" formControlName="email" [placeholder]="getString('anna@mail.se')" class="text-input"/>

        <div class="my-1 font-bold">{{ getString("Telefon") }}</div>
        <input type="tel" formControlName="phone" [placeholder]="getString('0707-777000')" class="text-input"/>

        <div class="my-1 font-bold">{{ getString("Födelsedag") }}</div>
        <input type="date" formControlName="birthday" [placeholder]="getString('2000-01-01')" class="text-input"/>

      </div>
      <div class="mt-4 flex flex-col gap-4">
        <app-checkbox-label [(ngModel)]="acceptPromotion" (ngModelChange)="saveFormToLocalStorage()">
          {{ getString("Skicka mig erbjudanden på födelsedag och andra specialerbjudanden") }}
        </app-checkbox-label>
        <app-checkbox-label [(ngModel)]="acceptAggreement" (ngModelChange)="saveFormToLocalStorage()">
          {{ getString("Godkänn allmänna villkor") }} <a (click)="openAgreement($event)"
                                                         class="text-theme-primary">{{ getString("läs mer") }}</a>
        </app-checkbox-label>
      </div>
      <div *ngIf="errorMsg" class="mt-4 text-theme-warning">{{ errorMsg }}</div>
      <div class="my-8 w-full">
        <app-action-button
          [text]="getString('Skaffa')"
          [disabled]="form.invalid || isWorking || !acceptAggreement"
          [icon]="'fa-circle-plus'"
          [chevron]="false"
          (onClick)="send()">
        </app-action-button>
      </div>
      <div *ngIf="isWorking"
           class="absolute bottom-0 top-0 left-0 right-0 bg-white/50 flex items-center justify-center">
        <app-loading-spinner></app-loading-spinner>
      </div>
    </div>
  `
})
export class SignupLoyaltyComponent {

  @Input() program!: LoyaltyConfigProgram;
  isWorking: boolean = false;
  form!: FormGroup
  errorMsg?: string
  acceptAggreement: boolean = false;
  acceptPromotion: boolean = false;

  constructor(public api: AuthorizedApiService, private translate: TranslateService, private auth: AuthService,
              private sheetService: BottomSheetService, private appStateService: AppStateService) {
  }

  private readonly STORAGE_KEY = 'loyalty_signup_form_data';

  ngOnInit() {
    this.form = new FormGroup({
      name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
      email: new FormControl('', {nonNullable: true, validators: []}),
      phone: new FormControl('', {nonNullable: true, validators: []}),
      birthday: new FormControl('', {nonNullable: true, validators: []}),
    }, {
      updateOn: 'change'
    });

    // Try to restore form data from localStorage first
    this.restoreFormFromLocalStorage();

    // If some fields are still empty, fill with data from appState
    this.appStateService.observeAppState().subscribe(appState => {
      // Only set default values if fields are empty
      if (!this.form.controls["name"].value) {
        this.form.controls["name"].setValue(appState.order.user_cfg.user_name);
      }
      if (!this.form.controls["email"].value) {
        this.form.controls["email"].setValue(appState.order.user_cfg.email);
      }
      if (!this.form.controls["phone"].value) {
        this.form.controls["phone"].setValue(appState.order.user_cfg.phone);
      }
    });

    // Save form data to localStorage whenever it changes
    this.form.valueChanges.subscribe(() => {
      this.saveFormToLocalStorage();
    });
  }

  // Save the form data to localStorage
  public saveFormToLocalStorage(): void {
    // Make sure the form is initialized
    if (!this.form) return;

    const formData = {
      ...this.form.value,
      acceptAggreement: this.acceptAggreement,
      acceptSendInfo: this.acceptPromotion
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(formData));
  }

  // Restore form data from localStorage
  private restoreFormFromLocalStorage(): void {
    try {
      const savedData = localStorage.getItem(this.STORAGE_KEY);
      if (savedData) {
        const formData = JSON.parse(savedData);

        // Restore form field values
        this.form.controls["name"].setValue(formData.name || '');
        this.form.controls["email"].setValue(formData.email || '');
        this.form.controls["phone"].setValue(formData.phone || '');
        this.form.controls["birthday"].setValue(formData.birthday || '');

        // Restore checkbox values
        this.acceptAggreement = formData.acceptAggreement || false;
        this.acceptPromotion = formData.acceptSendInfo || false;
      }
    } catch (error) {
      console.error('Error restoring form data from localStorage:', error);
      // Continue without restored data if there's an error
    }
  }

  getString(s: string) {
    return this.translate.getString(s)
  }

  get numberControl() {
    return this.form.controls["email"]!
  }

  send() {
    this.isWorking = true
    const name = this.form.controls["name"].value;
    const email = this.form.controls["email"].value;
    const phone = this.form.controls["phone"].value;
    const birthday = this.form.controls["birthday"].value;
    this.api.signupLoyaltyCard(this.program.id, name, email, phone, birthday, this.acceptPromotion).subscribe({
      next: () => {
        // Clear the saved form data from localStorage upon successful submission
        localStorage.removeItem(this.STORAGE_KEY);
        this.isWorking = false
        this.sheetService.popSheet();
      },
      error: err => {
        console.log('Error:', err);
        this.isWorking = false
        this.errorMsg = err?.error?.message || this.getString("Något gick fel")
      }
    })
  }

  openAgreement(event: MouseEvent) {
    // Stop the event from propagating to prevent toggling the checkbox
    event.stopPropagation();
    event.preventDefault();

    // Save form data before navigating away
    this.saveFormToLocalStorage();

    // Open the agreement sheet
    this.sheetService.pushSheet({sheetClass: "loyalty-agreement", title: "Villkor"});
  }

}
